import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '../../shared_components/UI/Box'

import {
  selectDiscordWarningNotice,
  selectIsDarkThemeState,
  selectTwitterWarningNotice,
} from '../../modules/miscUi/selectors'
import {
  setHeaderBgUrl,
  setShowHeaderGradient,
} from '../../modules/miscUi/actions'

import { Container, EmailInput } from './styles'
import { selectUser } from '../../modules/user/selectors'
import { theseus } from '../../api/theseus'
import { toast } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'
import EmbeddableModal, {
  ModalText,
} from '../../shared_components/EmbeddableModal'
import { Success } from '../../shared_components/Missions/Types/ImageVerification/Page'
import ConfettiExplosion from 'react-confetti-explosion'
import { ThemedButton } from '../../shared_components/ThemedButton'
import TwitterConnectButton from '../../shared_components/SocialConnectButtons/Twitter'
import DiscordConnectButton from '../../shared_components/SocialConnectButtons/Discord'
import TiktokConnectButton from '../../shared_components/SocialConnectButtons/Tiktok'
import GoogleConnectButton from '../../shared_components/SocialConnectButtons/Google'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import AnimatedLoader from '../../shared_components/AnimatedLoader'
import styled from 'styled-components'

import { setEmailValues, setLithiumAccount } from '../../modules/user/slice'
import TelegramConnectButton from '../../shared_components/SocialConnectButtons/Telegram'
import { useRefetchUpdateUser } from '../TwitterAuth'
import useBotPrevention from '../../shared_components/BotPrevention/useBotPrevention'

import Switch from '../../shared_components/Library/Switch'
import { useToggleIsPublicTradesMutation } from '../../modules/user/api'
import { H2, Span, Strong } from '../../shared_components/Library/Typeography'
import { Card, Divider } from '../../shared_components/Library/Card'
import { useShouldShowForThreshold } from '../../theme'
import ConnectWalletButton from '../../shared_components/Library/Buttons/ConnectWalletButton'
import { BackButton } from '../../shared_components/Page'

const Socials = () => {
  const { isLoggedIn, isAuthenticating } = useIsLoggedIn()
  const isDarkTheme = useSelector(selectIsDarkThemeState)

  const isAtLeastMediumScreen = useShouldShowForThreshold('medium')

  const dispatch = useDispatch()

  const [emailAddress, setEmailAddress] = useState('')
  const [emailError, setEmailError] = useState('')
  const { turnstileToken, Turnstile } = useBotPrevention()

  const twitterWarningNotice = useSelector(selectTwitterWarningNotice)
  const discordWarningNotice = useSelector(selectDiscordWarningNotice)

  const handleEmail = (e: ChangeEvent<HTMLInputElement>) =>
    setEmailAddress(e.target.value.trim())

  useEffect(() => {
    dispatch(setHeaderBgUrl(`/hero-cover-labs.png`))
    dispatch(setShowHeaderGradient(false))
    return () => {
      dispatch(setHeaderBgUrl(''))
    }
  }, [isDarkTheme, dispatch])

  const user = useSelector(selectUser)
  const userHasEmail = !!user?.emailAddress

  const twitterHandle = user?.twitterHandle
  const isInfluencer = user?.metadata?.isInfluencer
  const _isPublicTrades = !!user?.metadata?.memecoins?.isPublicTrades
  const [isPublicTrades, setIsPublicTrades] = useState(false)
  const [toggleIsPublicTrades, { isLoading: isLoadingToggle }] =
    useToggleIsPublicTradesMutation()

  useEffect(() => {
    setIsPublicTrades(_isPublicTrades)
  }, [_isPublicTrades])

  const handleToggleIsPublicTrades = async (value: boolean) => {
    try {
      const { isPublicTrades } = await toggleIsPublicTrades({
        body: {
          isPublicTrades: value,
        },
      }).unwrap()
      setIsPublicTrades(isPublicTrades)
      dispatch(
        setLithiumAccount({
          ...user,
          metadata: {
            ...user.metadata,
            memecoins: { ...user.metadata.memecoins, isPublicTrades },
          },
        }),
      )
      toast.success('Your preferences have been saved')
    } catch (e) {
      console.error(e)
      toast.error('Something went wrong. Please try again.')
    }
  }

  const refetchUpdateUser = useRefetchUpdateUser()
  const history = useHistory()
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const emailVerifiedParam = searchParams.get('email-verified')
  const emailVerifiedErrorParam = searchParams.get('error')

  useEffect(() => {
    const hash = location.hash
    if (!hash) return

    const element = document.getElementById(hash.substring(1))

    if (!element) return

    element.scrollIntoView({ behavior: 'smooth' })
  }, [location])

  const dismissModal = useCallback(() => {
    if (searchParams.has('error')) {
      searchParams.delete('error')
    }
    if (searchParams.has('email-verified')) {
      searchParams.delete('email-verified')
    }
    history.replace({
      search: searchParams.toString(),
    })
  }, [history, searchParams])

  useEffect(() => {
    setEmailAddress(user?.emailAddress || '')
  }, [user])

  const verifyEmailAddress = useCallback(async () => {
    try {
      setEmailError('')
      const response = await theseus.post('/auth/email/verify', {
        emailAddress,
        redirectUrl: window.location.href,
        turnstileToken,
      })
      if (response.status !== 200) {
        // response here will be of type AxiosError
        // It's falling here and not in .catch() because we are not returning
        //  a Promise.reject() in the axios interceptor
        // @ts-ignore
        const { status, statusText, data } = response.response || {}
        const hint = status === 401 ? 'Try to log in again' : ''
        toast.error(
          <>
            <div>
              {`${statusText ?? 'Error'}: ${data?.message ??
                data?.error ??
                'Something went wrong. Please try again later.'
                }`}
            </div>
            {hint && <div>{hint}</div>}
          </>,
        )
        return
      }
      if (response.data.message === 'error') {
        setEmailError(response.data.error)
        return
      }
      toast.info('Please check your email for the verification link')
    } catch (e) {
      console.error(e)
    }
  }, [emailAddress, turnstileToken])

  useEffect(() => {
    refetchUpdateUser()
  }, [])

  const disconnectEmail = useCallback(async () => {
    try {
      setEmailError('')
      const response = await theseus.post('/auth/email/disconnect')
      dispatch(setEmailValues({ emailAddress: '', hasVerifiedEmail: false }))
      toast.info('Email has been disconnected')
    } catch (e) {
      console.error(e)
    }
  }, [dispatch])

  if (!isLoggedIn && !isAuthenticating) return <LogoutSelfDestruct />

  if (!user) {
    return <ConnectWalletButton />
  }

  return (
    <Box jc>
      <Box dc g={32} mw="628px">
        <Box dc g={16}>
          {/* {!isAtLeastMediumScreen && <BackButton />} */}
          <H2 s="XL" fw="semibold" c="text-primary">
            Connect Twitter
          </H2>
          <Card ac g={12} pad="m">
            <Box dc g={16}>
              <Box ac g={12} fu aw>
                <img
                  src={`/supported-socials/twitter.svg`}
                  width={32}
                  height={32}
                  alt="Twitter logo"
                />
                <Box dc>
                  <Strong s="L">Connect your X account</Strong>
                  <Span s="S" c="text-secondary">
                    Connect your X account to access all missions
                  </Span>
                  {twitterWarningNotice && (
                    <span className="warning">{twitterWarningNotice}</span>
                  )}
                </Box>
              </Box>
              {!!twitterHandle && isInfluencer && (
                <Box dc>
                  <Divider />
                  <Box ac padT={16}>
                    <Box dc g={4}>
                      <Strong s="M">Show ZAP Launch trades publicly</Strong>
                      <Span s="S" c="text-secondary">
                        Because you have over 10k followers and Twitter Blue
                        Check, you can enable Influencer Trading. If enabled all
                        trades on ZAP Launch will be shown connected to your
                        Twitter account
                      </Span>
                    </Box>
                    <Box fu aw>
                      <Switch
                        checked={isPublicTrades}
                        onChange={handleToggleIsPublicTrades}
                        isLoading={isLoadingToggle}
                        disabled={!twitterHandle || isLoadingToggle}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              <Divider />
              <Box jfe>
                <TwitterConnectButton />
              </Box>
            </Box>
          </Card>
        </Box>
        <Divider />

        <Box dc g={16}>
          <H2 s="XL" fw="semibold" c="text-primary">
            Other accounts
          </H2>
          <Box dc g={20}>
            <Card ac g={12} pad="m">
              <Box
                g={12}
                fw
                ac={isAtLeastMediumScreen}
                dc={!isAtLeastMediumScreen}
              >
                <Box ac g={12}>
                  <img
                    src={`/supported-socials/tiktok.svg`}
                    width={32}
                    height={32}
                    alt="Tiktok logo"
                  />
                  <Box dc>
                    <Strong s="L">Connect your Tiktok account</Strong>
                    <Span s="S" c="text-secondary">
                      To access all missions.
                    </Span>
                  </Box>
                </Box>
                <Box jfe fu aw={isAtLeastMediumScreen}>
                  <TiktokConnectButton />
                </Box>
              </Box>
            </Card>
            <Card ac g={12} pad="m">
              <Box
                g={12}
                fw
                ac={isAtLeastMediumScreen}
                dc={!isAtLeastMediumScreen}
              >
                <Box ac g={12}>
                  <img
                    src={`/supported-socials/google.svg`}
                    width={32}
                    height={32}
                    alt="Google logo"
                  />
                  <Box dc>
                    <Strong s="L">Connect your Google account</Strong>
                    <Span s="S" c="text-secondary">
                      To access all missions.
                    </Span>
                  </Box>
                </Box>
                <Box jfe fu aw={isAtLeastMediumScreen}>
                  <GoogleConnectButton />
                </Box>
              </Box>
            </Card>
            <Card ac g={12} pad="m">
              <Box
                g={12}
                fw
                ac={isAtLeastMediumScreen}
                dc={!isAtLeastMediumScreen}
              >
                <Box ac g={12}>
                  <img
                    src={`/supported-socials/discord.svg`}
                    width={32}
                    height={32}
                    alt="Discord logo"
                  />
                  <Box dc>
                    <Strong s="L">Connect your Discord account</Strong>
                    <Span s="S" c="text-secondary">
                      To access all missions.
                    </Span>
                    {discordWarningNotice && (
                      <span className="warning">{discordWarningNotice}</span>
                    )}
                  </Box>
                </Box>
                <Box jfe fu aw={isAtLeastMediumScreen}>
                  <DiscordConnectButton />
                </Box>
              </Box>
            </Card>
            <Card ac g={12} pad="m">
              <Box
                g={12}
                fw
                ac={isAtLeastMediumScreen}
                dc={!isAtLeastMediumScreen}
              >
                <Box ac g={12}>
                  <img
                    src={`/supported-socials/telegram.svg`}
                    width={32}
                    height={32}
                    alt="telegram logo"
                  />
                  <Box dc>
                    <Strong s="L">Connect your Telegram account</Strong>
                    <Span s="S" c="text-secondary">
                      To access all missions.
                    </Span>
                  </Box>
                </Box>
                <Box jfe aw={isAtLeastMediumScreen} fu>
                  <TelegramConnectButton />
                </Box>
              </Box>
            </Card>
            <Card ac g={12} pad="m">
              <Box
                g={12}
                fw
                ac={isAtLeastMediumScreen}
                dc={!isAtLeastMediumScreen}
              >
                <Box ac g={12}>
                  <img
                    src={`/supported-socials/newsletter.svg`}
                    width={32}
                    height={32}
                    alt="Email icon"
                  />
                  <Box dc>
                    <Strong s="L">Connect your email</Strong>
                    <Span s="S" c="text-secondary">
                      To access all missions.
                    </Span>
                  </Box>
                </Box>
              </Box>
              <Box jfe>
                {userHasEmail && emailAddress && (
                  <Box g={12} ac jfe>
                    <Box dc afe g={8} fu aw>
                      <span>{emailAddress}</span>
                    </Box>
                    <ThemedButton
                      $styleType="surface-chip"
                      onClick={disconnectEmail}
                    >
                      Disconnect
                    </ThemedButton>
                  </Box>
                )}
                {!userHasEmail && (
                  <Box g={12} afs jfe>
                    <Box dc afe g={16} fu aw={isAtLeastMediumScreen}>
                      <EmailInput
                        placeholder="Your email address"
                        onChange={handleEmail}
                        value={emailAddress}
                        type="email"
                        $width="400px"
                      />
                      {emailError && (
                        <span className="error">{emailError}</span>
                      )}

                      <Turnstile />

                      <ThemedButton
                        $styleType="surface-chip"
                        disabled={
                          !isValidEmail(emailAddress) || !turnstileToken
                        }
                        onClick={verifyEmailAddress}
                      >
                        Connect
                      </ThemedButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </Box>
      </Box>

      <EmbeddableModal dismiss={dismissModal} isVisible={!!emailVerifiedParam}>
        {!!emailVerifiedParam && (
          <Success dc ac alignSelf="stretch" jc g={32}>
            {emailVerifiedParam === 'true' && (
              <ModalText>
                Your email <i>{emailAddress}</i> has been verified successfully!
              </ModalText>
            )}
            {emailVerifiedParam !== 'true' && (
              <ModalText>
                {emailVerifiedErrorParam ||
                  'Something went wrong. Please try again later.'}
              </ModalText>
            )}
            <Box jc fu>
              <ThemedButton onClick={dismissModal}>Close</ThemedButton>
              {emailVerifiedParam === 'true' && (
                <ConfettiExplosion zIndex={10000} />
              )}
            </Box>
          </Success>
        )}
      </EmbeddableModal>
    </Box>
  )
}

export default Socials

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

export function isValidEmail(email: string) {
  return emailRegex.test(email)
}

const Outer = styled(Box)`
  min-height: 50vh;
`

function LogoutSelfDestruct() {
  useEffect(() => {
    let timer = setTimeout(() => {
      window.location.href = '/'
      timer = null
    }, 1000)
    return () => {
      clearTimeout(timer)
      timer = null
    }
  }, [])
  return (
    <Container>
      <Outer c>
        <AnimatedLoader onlyWhite />
      </Outer>
    </Container>
  )
}
